/*
 * The formatted composer (F-2 §6, §13): a ProseMirror view over the dialect's schema inside the
 * composer frame every surface shares, with the toolbar above it and the one autocomplete list the
 * textarea uses beside it. What it stores is markdown and nothing else — the document lives in
 * memory, is serialized into the draft on every change, and follows the draft whenever the draft
 * changes from outside (`useEditorView`). The keyboard is two layers: the nine formatting chords go
 * by physical key code through `shortcutFor`, everything else through the keymap, and the
 * autocomplete answers first while its list is open. None of it is registered with the shell's
 * shortcut registry (F-2 D9): a registry matches `key`, and Ctrl+Shift+8 is not `*` on a German
 * keyboard.
 */

import { baseKeymap } from "prosemirror-commands"
import { history } from "prosemirror-history"
import { keymap } from "prosemirror-keymap"
import type { Node } from "prosemirror-model"
import { TextSelection } from "prosemirror-state"
import type { EditorView } from "prosemirror-view"
import {
	useCallback,
	useEffect,
	useId,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { Button, type MenuItem } from "../../ui/index.ts"
import { AutocompleteList } from "../composer/autocomplete/AutocompleteList.tsx"
import { useAutocomplete } from "../composer/autocomplete/useAutocomplete.ts"
import { AttachControl, ComposerFrame } from "../composer/ComposerFrame.tsx"
import { setSourceMode } from "../composer/settings.ts"
import { shortcutFor } from "../composer/text/shortcuts.ts"
import type { ComposerSurfaceProps } from "../composer/useComposerCore.ts"
import { currentOutbox } from "../drafts/outbox.ts"
import { t, translate } from "../i18n/index.ts"
import { Icon, useAnnounce, useShortcutRegistry } from "../shell/index.ts"
import type { ActionId } from "./actions.ts"
import { autocompleteKey, autocompletePlugin } from "./autocomplete.ts"
import { writeClipboardText } from "./clipboard.ts"
import { tokenDecorations } from "./decorations.ts"
import "./editor.css"
import { emojiCharacter } from "./emoji.ts"
import { buildInputRules } from "./inputRules.ts"
import { buildKeymap, enterAddsListItem } from "./keymap.ts"
import { docIsBlank } from "./kit.ts"
import { LinkDialog, type LinkRequest } from "./LinkDialog.tsx"
import { messageActions, messageKit } from "./message.ts"
import { handlePaste } from "./paste.ts"
import { Toolbar } from "./Toolbar.tsx"
import { useEditorView, type EditorBinding } from "./useEditorView.ts"

/**
 * The document position `offset` characters into the document's own text, clamped to its end.
 *
 * It is how the caret survives the swap from the stand-in (F-2 D6): what the stand-in counted is an
 * offset into markdown, and what the editor needs is a position in a tree. The two agree exactly
 * for the case this restores — somebody typing a sentence while the chunk is still on its way —
 * and where they do not (a caret inside `**` markers), the end of the text is the honest answer,
 * which is what falling off the end of this walk gives.
 */
function positionAtTextOffset(doc: Node, offset: number): number {
	let seen = 0
	let found: number | null = null
	doc.descendants((node, pos) => {
		if (found !== null) return false
		if (!node.isText) return true
		const length = node.text?.length ?? 0
		if (seen + length >= offset) found = pos + (offset - seen)
		seen += length
		return false
	})
	return found ?? doc.content.size
}

export function EditorComposer(props: ComposerSurfaceProps) {
	const {
		core,
		targetKey,
		placeholderKey,
		placeholderArgs,
		sources,
		renderEmojiPicker,
		toolbar = "full",
		autoFocus,
		takeCaret,
		ref,
	} = props
	const hostId = useId()
	const label = translate(placeholderKey, placeholderArgs)
	const announce = useAnnounce()
	const registry = useShortcutRegistry()

	// The plugins and the handlers are built once per view and reach the binding through this
	// holder, which exists before the binding does.
	const bindingRef = useRef<EditorBinding | null>(null)
	const { draft, attachments, value, setValue, disabled } = core

	// The bindings built once per view read the latest render through these.
	const coreRef = useRef(core)
	const propsRef = useRef(props)
	useLayoutEffect(() => {
		coreRef.current = core
		propsRef.current = props
	})

	const [focused, setFocused] = useState(false)
	const [linkRequest, setLinkRequest] = useState<LinkRequest | null>(null)
	const [rowOpen, setRowOpen] = useState(false)
	const shiftHeld = useRef(false)

	const plugins = () => [
		buildInputRules(messageKit, { emoji: emojiCharacter }),
		history(),
		autocompletePlugin(messageKit),
		tokenDecorations(messageKit),
		keymap(
			buildKeymap(messageKit, {
				send: () => coreRef.current.submit(),
				enterSends: () => coreRef.current.settings.enterSends,
				outboxUndo: () => {
					void currentOutbox()?.undoLatest(propsRef.current.targetKey)
				},
				editLast: () => {
					const open = propsRef.current.onEditLast
					if (open === undefined) return false
					open()
					return true
				},
			}),
		),
		keymap(baseKeymap),
	]

	const autocompleteRef = useRef<{ onKeyDown(e: KeyboardEvent): boolean }>({
		onKeyDown: () => false,
	})

	const runAction = (id: ActionId) => {
		const view = bindingRef.current?.view() ?? null
		if (view === null) return
		messageActions.byId[id].run({
			openLink: () => setLinkRequest(messageActions.linkRequestOf(view.state)),
		})(view.state, view.dispatch, view)
		view.focus()
	}

	const host = useRef<HTMLDivElement>(null)
	const binding = useEditorView(host, {
		value,
		ready: draft.ready,
		resetKey: targetKey,
		editable: !disabled,
		onChange: (markdown) => {
			setValue(markdown)
			core.clearFailure()
		},
		plugins,
		handlers: () => ({
			handleKeyDown: (_view, event) => {
				shiftHeld.current = event.shiftKey
				if (autocompleteRef.current.onKeyDown(event)) return true
				const id = shortcutFor(event)
				if (id === null) return false
				runAction(id)
				return true
			},
			handlePaste: (view, event) =>
				handlePaste(messageKit, view, event, {
					attach: (files) => attachments.attach(files),
					literal: () => shiftHeld.current,
				}),
			clipboardTextSerializer: (slice) => messageKit.clipboardMarkdown(slice),
			handleDOMEvents: {
				// Escape is answered here rather than in the keymap, and the difference is what the
				// key does when this composer has no use for it. prosemirror-view claims Escape
				// unconditionally (`captureKeyDown`) and calls preventDefault even when no command
				// took it, and the shell's region listener — which is what closes the details
				// column — answers only an Escape nothing has prevented (UX §2.3). A handler that
				// returns true here is the one place the view skips its own keydown handling
				// altogether, so an Escape this box does not use travels on untouched.
				keydown: (_view, event) => {
					if (event.key !== "Escape") return false
					if (autocompleteRef.current.onKeyDown(event)) return true
					const cancel = coreRef.current.cancel
					if (cancel === undefined) return true
					cancel()
					event.preventDefault()
					return true
				},
				focus: () => {
					setFocused(true)
					return false
				},
				blur: () => {
					setFocused(false)
					void draft.flush()
					return false
				},
			},
		}),
	})
	useLayoutEffect(() => {
		bindingRef.current = binding
	})

	const { state } = binding
	const blank = state === null || docIsBlank(state)

	// The autocomplete: the caret's textblock up to the caret, and the range it names mapped back.
	const context = state === null ? null : (autocompleteKey.getState(state) ?? null)
	const textBefore = context?.textBefore ?? ""
	const onReplace = useCallback((range: { start: number; end: number }, insert: string) => {
		const v = bindingRef.current?.view() ?? null
		const ctx = v === null ? null : autocompleteKey.getState(v.state)
		if (v === null || ctx === null || ctx === undefined) return
		v.dispatch(v.state.tr.insertText(insert, ctx.from + range.start, ctx.from + range.end))
		v.focus()
	}, [])
	const autocomplete = useAutocomplete({
		sources,
		value: textBefore,
		caret: context !== null && focused ? textBefore.length : null,
		rich: true,
		// The context is one block's text; a source anchored at the message's start (a command
		// palette) may open only in the document's first block, whose content starts at position 1.
		startsMessage: context?.from === 1,
		onReplace,
	})
	useLayoutEffect(() => {
		autocompleteRef.current = autocomplete
	})

	// The host's attributes: the accessible name, and the combobox state while a list is open.
	const attributes: Record<string, string> = {
		id: hostId,
		class: "tb-composer__input tb-editor",
		// While the suggestion list is open the box *is* the combobox of ARIA 1.2's pattern, and the
		// attributes below belong to that role and not to `textbox` (aria-expanded, aria-haspopup).
		// The source view says `combobox` at all times because its element is a textarea, which is
		// multi-line by nature; this one says so only while it has a popup to describe.
		role: autocomplete.open ? "combobox" : "textbox",
		...(autocomplete.open ? {} : { "aria-multiline": "true" }),
		"aria-label": label,
		"aria-describedby": core.describedBy,
		...(core.tooLong ? { "aria-invalid": "true" } : {}),
		// A contenteditable reads `tabIndex` −1 unless it says otherwise, and a focus trap — a dialog's —
		// keeps only what reads 0 or more: without this the box a dialog is for is out of its Tab loop.
		tabindex: "0",
		spellcheck: "true",
		autocapitalize: "sentences",
		autocomplete: "off",
		"data-placeholder": label,
		...(blank ? { "data-empty": "true" } : {}),
		...(autocomplete.open
			? {
					"aria-haspopup": "listbox",
					"aria-autocomplete": "list",
					"aria-expanded": "true",
					"aria-controls": autocomplete.listboxId,
					"aria-activedescendant": autocomplete.optionId(autocomplete.activeIndex),
				}
			: {}),
	}
	const attributeKey = JSON.stringify(attributes)
	const { setAttributes } = binding
	useEffect(() => {
		setAttributes(JSON.parse(attributeKey) as Record<string, string>)
	}, [attributeKey, setAttributes])

	// Where the list stands: under the trigger character, clamped to the box. Measured once the
	// document is laid out, which is after the render that opened the list.
	const [listOffset, setListOffset] = useState<number | undefined>(undefined)
	const { triggerStart } = autocomplete
	const from = context?.from
	useLayoutEffect(() => {
		const v = bindingRef.current?.view() ?? null
		const box = v?.dom.closest(".tb-composer__box")
		if (v === null || from === undefined || triggerStart === null || !box) {
			setListOffset(undefined)
			return
		}
		const boxRect = box.getBoundingClientRect()
		const caret = v.coordsAtPos(from + triggerStart)
		setListOffset(
			Math.max(0, Math.min(caret.left - boxRect.left, Math.max(0, boxRect.width - 280))),
		)
	}, [from, triggerStart])

	useImperativeHandle(
		ref,
		() => ({
			focus: () => binding.focus(),
			getValue: () => binding.markdown(),
			setValue: (v) => setValue(v),
			insertAtCaret: (text) => {
				const v = binding.view()
				if (v === null) return
				v.dispatch(v.state.tr.insertText(text).scrollIntoView())
			},
			caretInFencedBlock: () =>
				binding.view()?.state.selection.$from.parent.type === messageKit.N.codeBlock,
		}),
		[binding, setValue],
	)

	// The caret is taken once, when the box appears. The dependency is the focus callback and not
	// the binding: `useEditorView` builds a fresh binding object on every render, and an effect
	// depending on it runs after every render — the pane's own blur re-renders the composer, which
	// would then take the focus straight back and trap the keyboard in it (WCAG 2.1.2).
	const focusBox = binding.focus
	useEffect(() => {
		if (autoFocus === true) focusBox()
	}, [autoFocus, focusBox])

	// And once more for the one case `autoFocus` does not cover: this box has just replaced the
	// stand-in a person was typing into (F-2 D6, X-A2 D6). The element that held the caret is gone
	// from the document, so without this the next keystrokes go to `document.body` — seconds' worth
	// of them on a slow line.
	//
	// Once per view, and not once per note: a view is built for a box that has just appeared, and
	// every other render leaves it alone, so the pane's own blur cannot take the focus back and trap
	// the keyboard in it (WCAG 2.1.2). Development's double mount builds two views, and the second
	// one is the one on the screen — restoring into the first alone is why the keystrokes after the
	// swap went nowhere there. The note's own age is what stops it reaching a view built later for a
	// reason of its own (`Composer.tsx`).
	const restoredInto = useRef<EditorView | null>(null)
	useEffect(() => {
		// Not before the draft has answered: until then the document is empty, and a caret restored
		// into an empty document is a caret at position 0 of the text that arrives a frame later.
		if (takeCaret === undefined || !draft.ready) return
		const view = binding.view()
		if (view === null || restoredInto.current === view) return
		const note = takeCaret()
		if (note === null) return
		restoredInto.current = view
		const at = view.state.doc.resolve(positionAtTextOffset(view.state.doc, note.caret))
		view.dispatch(view.state.tr.setSelection(TextSelection.near(at)).scrollIntoView())
		view.focus()
	}, [binding, draft.ready, takeCaret])

	const applyLink = (text: string, href: string) => {
		const v = binding.view()
		const request = linkRequest
		setLinkRequest(null)
		if (v === null || request === null) return
		v.dispatch(messageActions.applyLink(v.state, request, text, href))
		announce("composer.link.inserted")
	}

	const removeLink = () => {
		const v = binding.view()
		const request = linkRequest
		setLinkRequest(null)
		if (v === null || request === null) return
		v.dispatch(messageActions.removeLink(v.state, request))
		announce("composer.link.removed")
	}

	const insertEmoji = (char: string) => {
		const v = binding.view()
		if (v === null) return
		v.dispatch(v.state.tr.insertText(char).scrollIntoView())
		v.focus()
	}

	const copyPlain = () => {
		const v = binding.view()
		if (v === null) return
		void writeClipboardText(messageKit.plainTextOfSelection(v.state)).then((done) => {
			if (done) announce("composer.menu.copiedPlain")
		})
	}

	const sheet = registry.all().find((def) => def.id === "sheet")
	const menu: MenuItem[] = [
		{
			id: "sourceMode",
			label: t("composer.menu.sourceMode"),
			checked: false,
			onSelect: () => setSourceMode(true),
		},
		{ id: "copyPlain", label: t("composer.menu.copyPlain"), disabled: blank, onSelect: copyPlain },
		...(sheet === undefined
			? []
			: [{ id: "shortcuts", label: t("composer.menu.shortcuts"), onSelect: () => sheet.run() }]),
	]

	// Whether the row folds is the stylesheet's decision, not this component's: it depends on how
	// wide *this box* is, and a box in a details column is narrow inside a wide window. The class
	// says what the surface asked for, the container query adds every box under 640 px, and both
	// end at the same two rules in composer.css. `data-open` is the half React does own.
	const rowId = `${hostId}-toolbar`

	return (
		<>
			<ComposerFrame
				core={core}
				className={toolbar === "overflow" ? "tb-composer--fold" : undefined}
				belowTools={props.belowTools}
				// The row draws the attach control itself, between the emoji and the `⋯` (§7.6), so the
				// frame does not draw a second one — unless there is no row at all.
				attachInToolbar={toolbar !== "none"}
				// The third state of the hint under the box (X-B1 A7): inside a list Enter adds an item
				// whatever the send setting says, so the line says so while the caret is there. Where the
				// setting is already "Shift+Enter sends", Enter starts a line everywhere and the frame's
				// own sentence is true in a list as well.
				hint={
					state !== null && core.settings.enterSends && enterAddsListItem(messageKit, state)
						? "composer.hint.list"
						: undefined
				}
				toolbar={
					toolbar === "none" ? null : (
						<Toolbar
							hostId={hostId}
							rowId={rowId}
							rowOpen={rowOpen}
							state={state}
							actions={messageActions}
							onAction={runAction}
							renderEmojiPicker={renderEmojiPicker}
							onEmoji={insertEmoji}
							attach={(stop) => <AttachControl core={core} stop={stop} />}
							menu={menu}
						/>
					)
				}
				lead={
					toolbar === "none" ? null : (
						<Button
							quiet
							icon
							size="sm"
							className="tb-composer__tool tb-composer__fold"
							aria-label={rowOpen ? t("composer.format.hide") : t("composer.format.show")}
							// A disclosure over a region, not a two-state control: expanded says whether
							// the row it names is on screen. The stylesheet hides the button in a box
							// wide enough for the row, and a button that is not drawn is not announced.
							aria-expanded={rowOpen}
							aria-controls={rowId}
							before={<Icon name="textStyle" />}
							onClick={() => setRowOpen((open) => !open)}
						/>
					)
				}
			>
				<div ref={host} className="tb-editor__host" />
				<AutocompleteList state={autocomplete} offset={listOffset} />
			</ComposerFrame>
			<LinkDialog
				request={linkRequest}
				onClose={() => setLinkRequest(null)}
				onSubmit={applyLink}
				onRemove={removeLink}
				allowed={messageKit.dialect.allowedHref}
			/>
		</>
	)
}
